import { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { useTranslations } from '@components/context';
import Button from '@components/legacy/core/Button';
import MetaButton from '@components/core/MetaButton';
import CheckboxField from '@components/core/CheckboxField';
import Avatar from '@components/core/Avatar';
import Tooltip from '@components/core/Tooltip';
import { groupPeople, hasPendingAccess, isChecked } from './helper';

const MARGIN = 10;
const STEP_ACCESS_ICON = 'eye';
// The person's own gradient portrait, at the list's row size.
const STEP_ACCESS_AVATAR_VARIANT = 'initials';
const STEP_ACCESS_AVATAR_SIZE = 'xs';

// Measured from the trigger and placed in the viewport, because the list is portalled out of the
// editor: inside it, the applications-view modal's scrolling body clips anything absolute.
const positionFromAnchor = (anchor, popover) => {
    const a = anchor.getBoundingClientRect();
    const p = popover.getBoundingClientRect();
    let left = Math.min(a.left, window.innerWidth - p.width - MARGIN);
    let top = a.bottom + 4;
    if (top + p.height > window.innerHeight - MARGIN) {
        const above = a.top - p.height - 4;
        top = above > MARGIN ? above : Math.max(MARGIN, window.innerHeight - p.height - MARGIN);
    }
    return { top, left: Math.max(MARGIN, left) };
};

// Who may see one step. Controlled on `pending`: the ticks live in the caller, which also decides
// when they are written, so this file stays out of the save mechanics entirely.
const PersonRow = ({ stepId, person, checked, outside, onToggle, disabled, outsideLabel }) => (
    <div
        className="step-access-person"
        data-action={`${stepId}-step_access-person`}
        data-value={person.id}
        title={person.locked ? person.locked_reason || undefined : undefined}
    >
        <CheckboxField
            checkboxId={`${stepId}-step_access-${person.id}`}
            isChecked={checked}
            disabled={disabled || person.locked}
            onChange={() => !(disabled || person.locked) && onToggle(person)}
            label={(
                <>
                    <Avatar
                        variant={STEP_ACCESS_AVATAR_VARIANT}
                        size={STEP_ACCESS_AVATAR_SIZE}
                        name={person.text}
                        className="step-access-avatar"
                    />
                    <span className="step-access-person-name">{person.text}</span>
                </>
            )}
        />
        {outside && <span className="step-access-person-outside">{outsideLabel}</span>}
    </div>
);

const StepAccess = ({
    stepId,
    people = [],
    directory = [],
    pending = {},
    query = '',
    onQueryChange,
    onToggle,
    onCommit,
    editable = true,
    unsavedStep = false,
}) => {
    const { t } = useTranslations();
    const [open, setOpen] = useState(false);
    const [position, setPosition] = useState(null);
    const triggerRef = useRef(null);
    const popoverRef = useRef(null);

    // Save is the only write. Closing neither saves nor discards: the ticks stay pending in the
    // caller.
    const save = useCallback(async () => {
        const result = await onCommit();
        if (result?.ok !== false) {
            setOpen(false);
        }
    }, [onCommit]);

    const reposition = useCallback(() => {
        if (triggerRef.current && popoverRef.current) {
            setPosition(positionFromAnchor(triggerRef.current, popoverRef.current));
        }
    }, []);

    useLayoutEffect(() => {
        if (!open) {
            setPosition(null);
            return;
        }
        reposition();
    }, [open, reposition]);

    useEffect(() => {
        if (!open) {
            return undefined;
        }
        const onKey = (event) => event.key === 'Escape' && setOpen(false);
        // Capture, so scrolling the modal body that hosts the editor moves the list with the step.
        window.addEventListener('scroll', reposition, true);
        window.addEventListener('resize', reposition);
        document.addEventListener('keydown', onKey);
        return () => {
            window.removeEventListener('scroll', reposition, true);
            window.removeEventListener('resize', reposition);
            document.removeEventListener('keydown', onKey);
        };
    }, [open, reposition]);

    const { team, others } = groupPeople({ people, directory, query });
    // Access is stored against the step's permanent id, which a step created in this session does
    // not have until the flow is saved.
    const disabled = !editable || unsavedStep;

    return (
        <span className="step-access">
            {/* Renders inside the step card's meta row, beside the lead-time and deadline
                controls, and reads as one of them. */}
            <Tooltip title={t('Who can see the applicants in this step.')}>
                <MetaButton
                    ref={triggerRef}
                    icon={STEP_ACCESS_ICON}
                    active={open}
                    className="step-access-trigger"
                    data-action={`${stepId}-step_access-open`}
                    onClick={() => setOpen(!open)}
                >
                    {t('Visible to')}
                </MetaButton>
            </Tooltip>
            {open && createPortal(
                <>
                <div className="step-access-backdrop" onMouseDown={() => setOpen(false)} />
                <div
                    ref={popoverRef}
                    className="step-access-popover"
                    data-action={`${stepId}-step_access-popover`}
                    style={position
                        ? { top: `${position.top}px`, left: `${position.left}px`, visibility: 'visible' }
                        : { visibility: 'hidden' }}
                >
                    <span className="step-access-title">{t('Visible to')}</span>
                    {unsavedStep && (
                        <p className="step-access-help">{t('Save the recruitment flow before giving access to this step.')}</p>
                    )}
                    <div className="step-access-search">
                        <span className="icon-svg-search-sm no-spacing" aria-hidden="true" />
                        <input
                            type="search"
                            data-action={`${stepId}-step_access-search`}
                            placeholder={t('Search people to add')}
                            value={query}
                            disabled={disabled}
                            onChange={(event) => onQueryChange(event.target.value)}
                        />
                    </div>
                    <div className="step-access-list">
                        <span className="step-access-group">{t('Hiring team')}</span>
                        {team.map((person) => (
                            <PersonRow
                                key={person.id}
                                stepId={stepId}
                                person={person}
                                checked={isChecked(person, pending)}
                                onToggle={onToggle}
                                disabled={disabled}
                            />
                        ))}
                        {team.length === 0 && (
                            <span className="step-access-help">{t('No matches on the hiring team.')}</span>
                        )}
                        {others.length > 0 && (
                            <>
                                <span className="step-access-group">{t('Other people')}</span>
                                {others.map((person) => (
                                    <PersonRow
                                        key={person.id}
                                        stepId={stepId}
                                        person={person}
                                        checked={isChecked(person, pending)}
                                        outside
                                        outsideLabel={t('Not on team')}
                                        onToggle={onToggle}
                                        disabled={disabled}
                                    />
                                ))}
                            </>
                        )}
                    </div>
                    <p className="step-access-help">
                        {t('People from outside join the hiring team, for this step only.')}
                    </p>
                    <div className="step-access-footer">
                        <Button
                            v2
                            type="primary"
                            title={t('Save')}
                            data-action={`${stepId}-step_access-save`}
                            disabled={disabled || !hasPendingAccess(pending)}
                            onClick={save}
                        />
                    </div>
                </div>
                </>,
                document.body,
            )}
        </span>
    );
};

export default StepAccess;
